import type { PKParams } from '../pharmacology/types';

/** Converts a plasma concentration in mg/L to nanomolar given a molecular weight in g/mol. */
export function mgPerLToNanomolar(concentrationMgPerL: number, molecularWeightGMol: number): number {
  if (concentrationMgPerL <= 0) return 0;
  // mol/L = (mg/L * 1e-3 g/mg) / (g/mol); nM = mol/L * 1e9
  // => nM = concentrationMgPerL * 1e6 / molecularWeightGMol
  return (concentrationMgPerL * 1e6) / molecularWeightGMol;
}

/**
 * One-compartment oral absorption/elimination model.
 * C(t) = (F * Dose * ka) / (Vd * (ka - ke)) * (e^(-ke*t) - e^(-ka*t))
 * Returns plasma concentration in mg/L. Uses the analytical limit for the
 * degenerate ka === ke case (flip-flop kinetics) to avoid a division by zero.
 */
export function oralPlasmaConcentrationMgPerL(
  doseMg: number,
  tHours: number,
  params: { ka: number; ke: number; vd: number; bioavailability: number },
): number {
  if (doseMg <= 0 || tHours < 0) return 0;
  const { ka, ke, vd, bioavailability: F } = params;

  if (Math.abs(ka - ke) < 1e-9) {
    // Limit of the standard formula as ka -> ke.
    return (F * doseMg * ka) / vd * tHours * Math.exp(-ka * tHours);
  }

  return ((F * doseMg * ka) / (vd * (ka - ke))) * (Math.exp(-ke * tHours) - Math.exp(-ka * tHours));
}

/** Simple single-exponential decay from a starting concentration, in nanomolar. */
export function directReleaseConcentrationNM(startingConcentrationNM: number, tHours: number, ke: number): number {
  if (startingConcentrationNM <= 0 || tHours < 0) return 0;
  return startingConcentrationNM * Math.exp(-ke * tHours);
}

/**
 * Hybrid saturating-absorption / zero-order-elimination model, appropriate
 * for substances (like ethanol) that are cleared at a roughly constant rate
 * rather than a fixed half-life. The rising phase approaches the theoretical
 * peak plasma concentration (F*Dose/Vd) via a saturating exponential; the
 * falling phase subtracts a constant rate and is clamped at zero.
 * Returns plasma concentration in mg/L.
 */
export function zeroOrderEliminationConcentrationMgPerL(
  doseMg: number,
  tHours: number,
  params: { ka: number; vd: number; bioavailability: number; eliminationRateMgPerLPerHour: number },
): number {
  if (doseMg <= 0 || tHours < 0) return 0;
  const { ka, vd, bioavailability: F, eliminationRateMgPerLPerHour: k0 } = params;

  const peakConcentration = (F * doseMg) / vd;
  const absorbed = peakConcentration * (1 - Math.exp(-ka * tHours));
  const eliminated = k0 * tHours;

  return Math.max(0, absorbed - eliminated);
}

/**
 * Computes brain concentration in nanomolar for a substance at a given dose
 * (mg for oral-route/zero-order-elimination substances, or a direct release
 * level in nanomolar for direct-release/endogenous substances) and elapsed
 * time in hours.
 */
export function computeBrainConcentrationNM(pk: PKParams, doseOrReleaseLevel: number, tHours: number): number {
  if (pk.route === 'oral') {
    const plasmaMgPerL = oralPlasmaConcentrationMgPerL(doseOrReleaseLevel, tHours, pk);
    const plasmaNM = mgPerLToNanomolar(plasmaMgPerL, pk.molecularWeightGMol);
    return plasmaNM * pk.brainPlasmaRatio;
  }
  if (pk.route === 'zero-order-elimination') {
    const plasmaMgPerL = zeroOrderEliminationConcentrationMgPerL(doseOrReleaseLevel, tHours, pk);
    const plasmaNM = mgPerLToNanomolar(plasmaMgPerL, pk.molecularWeightGMol);
    return plasmaNM * pk.brainPlasmaRatio;
  }
  return directReleaseConcentrationNM(doseOrReleaseLevel, tHours, pk.ke);
}
